// Página de FUNCIONES: recorrido comercial y detallado por cada capacidad real
// de la plataforma (basado en los módulos reales de la app). Lenguaje sencillo,
// sin tecnicismos: cada bloque nombra el problema del coleccionista, lo que
// saands hace y el beneficio concreto. Solo usa imágenes verificadas (locales).

import { useTranslation } from 'react-i18next';
import { Icon } from '../../ui/components.js';
import { IMG } from '../media.js';
import {
  Container,
  CtaBanner,
  Eyebrow,
  FeatureCard,
  SectionHeading,
} from '../components.js';
import { softwareJsonLd, useSeo } from '../seo.js';

/** Bloque alterno imagen/texto para una capacidad grande. */
function Showcase({
  eyebrow,
  eyebrowIcon,
  title,
  lead,
  points,
  image,
  imageAlt,
  reverse = false,
}: {
  eyebrow: string;
  eyebrowIcon: string;
  title: string;
  lead: string;
  points: { icon: string; title: string; text: string }[];
  image: string;
  imageAlt: string;
  reverse?: boolean;
}) {
  return (
    <section className="py-space-xl">
      <Container className="grid lg:grid-cols-2 gap-space-xl items-center">
        <div className={`rounded-xl overflow-hidden border border-border-subtle ${reverse ? 'lg:order-2' : ''}`}>
          <img src={image} alt={imageAlt} className="w-full h-72 lg:h-[26rem] object-cover" loading="lazy" />
        </div>
        <div className={`flex flex-col gap-space-lg ${reverse ? 'lg:order-1' : ''}`}>
          <SectionHeading align="left" eyebrow={eyebrow} eyebrowIcon={eyebrowIcon} title={title} subtitle={lead} />
          <ul className="flex flex-col gap-space-md">
            {points.map((p) => (
              <li key={p.title} className="flex items-start gap-space-sm">
                <span className="w-9 h-9 shrink-0 rounded-lg bg-primary-container/10 border border-primary-container/30 text-primary flex items-center justify-center">
                  <Icon name={p.icon} className="text-[20px]" />
                </span>
                <div className="flex flex-col gap-0.5">
                  <span className="font-headline-sm text-headline-sm text-on-surface font-bold">{p.title}</span>
                  <span className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">{p.text}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  );
}

/** Columna con el título de un grupo (icono ámbar) y la lista de capacidades (checks azules). */
function ToolList({ icon, title, items }: { icon: string; title: string; items: string[] }) {
  return (
    <div className="rounded-xl border border-border-subtle bg-surface-deck p-space-lg flex flex-col gap-space-md">
      <div className="flex items-center gap-space-sm">
        <span className="w-11 h-11 shrink-0 rounded-lg bg-vacuum-glow/10 border border-vacuum-glow/30 text-vacuum-glow flex items-center justify-center">
          <Icon name={icon} className="text-[22px]" />
        </span>
        <h3 className="font-headline-sm text-headline-sm text-on-surface font-bold">{title}</h3>
      </div>
      <ul className="flex flex-col gap-space-sm">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-space-xs">
            <Icon name="check_circle" className="text-primary text-[18px] shrink-0 mt-0.5" />
            <span className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function FeaturesView() {
  const { t } = useTranslation();
  useSeo({
    title: t('marketing.features.seoTitle'),
    description: t('marketing.features.seoDescription'),
    path: '/funciones',
    image: IMG.recordsHeadphones,
    keywords: t('marketing.features.seoKeywords', { returnObjects: true }) as string[],
    jsonLd: [softwareJsonLd()],
  });

  const catalogItems = t('marketing.features.catalogItems', { returnObjects: true }) as string[];
  const configItems = t('marketing.features.configItems', { returnObjects: true }) as string[];
  const adminItems = t('marketing.features.adminItems', { returnObjects: true }) as string[];

  return (
    <>
      {/* ===== HERO de funciones ===== */}
      <section className="relative overflow-hidden border-b border-border-subtle">
        <div className="absolute inset-0 pointer-events-none">
          <img src={IMG.recordsHeadphones} alt="" aria-hidden className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-surface-base/85 via-surface-base/92 to-surface-base" />
        </div>
        <Container className="relative py-space-xl lg:py-24 flex flex-col items-center text-center gap-space-md">
          <Eyebrow icon="widgets">{t('marketing.features.heroEyebrow')}</Eyebrow>
          <h1 className="font-display-hero text-display-hero-mobile lg:text-display-hero text-on-surface font-bold tracking-tight max-w-4xl text-balance">
            {t('marketing.features.heroTitlePre')}<span className="text-primary">{t('marketing.features.heroTitleHighlight')}</span>
          </h1>
          <p className="font-body-md text-body-lg text-on-surface-variant max-w-2xl leading-relaxed">
            {t('marketing.features.heroSubtitle')}
          </p>
        </Container>
      </section>

      {/* ===== 1. Agregar discos ===== */}
      <Showcase
        eyebrow={t('marketing.features.s1Eyebrow')}
        eyebrowIcon="add_a_photo"
        title={t('marketing.features.s1Title')}
        lead={t('marketing.features.s1Lead')}
        image={IMG.holdingRecord}
        imageAlt={t('marketing.features.s1ImageAlt')}
        points={[
          { icon: 'photo_camera', title: t('marketing.features.s1p1Title'), text: t('marketing.features.s1p1Text') },
          { icon: 'mic', title: t('marketing.features.s1p2Title'), text: t('marketing.features.s1p2Text') },
          { icon: 'edit_note', title: t('marketing.features.s1p3Title'), text: t('marketing.features.s1p3Text') },
        ]}
      />

      {/* ===== 2. Fichas completas (Discogs) ===== */}
      <Showcase
        reverse
        eyebrow={t('marketing.features.s2Eyebrow')}
        eyebrowIcon="auto_awesome"
        title={t('marketing.features.s2Title')}
        lead={t('marketing.features.s2Lead')}
        image={IMG.turntable}
        imageAlt={t('marketing.features.s2ImageAlt')}
        points={[
          { icon: 'auto_awesome', title: t('marketing.features.s2p1Title'), text: t('marketing.features.s2p1Text') },
          { icon: 'payments', title: t('marketing.features.s2p2Title'), text: t('marketing.features.s2p2Text') },
          { icon: 'checklist', title: t('marketing.features.s2p3Title'), text: t('marketing.features.s2p3Text') },
        ]}
      />

      {/* ===== 3. Orden y control ===== */}
      <section className="py-space-xl bg-surface-deck border-y border-border-subtle">
        <Container className="flex flex-col gap-space-xl">
          <SectionHeading
            eyebrow={t('marketing.features.orderEyebrow')}
            eyebrowIcon="shelves"
            title={t('marketing.features.orderTitle')}
            subtitle={t('marketing.features.orderSubtitle')}
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-space-md">
            <FeatureCard icon="content_copy" title={t('marketing.features.o1Title')} accent="amber">
              {t('marketing.features.o1Body')}
            </FeatureCard>
            <FeatureCard icon="shelves" title={t('marketing.features.o2Title')} accent="amber">
              {t('marketing.features.o2Body')}
            </FeatureCard>
            <FeatureCard icon="search" title={t('marketing.features.o3Title')} accent="amber">
              {t('marketing.features.o3Body')}
            </FeatureCard>
            <FeatureCard icon="category" title={t('marketing.features.o4Title')} accent="amber">
              {t('marketing.features.o4Body')}
            </FeatureCard>
            <FeatureCard icon="bookmark" title={t('marketing.features.o5Title')} accent="amber">
              {t('marketing.features.o5Body')}
            </FeatureCard>
            <FeatureCard icon="history" title={t('marketing.features.o6Title')} accent="amber">
              {t('marketing.features.o6Body')}
            </FeatureCard>
          </div>
        </Container>
      </section>

      {/* ===== 4. Saber qué tienes y cazar ofertas ===== */}
      <Showcase
        eyebrow={t('marketing.features.s4Eyebrow')}
        eyebrowIcon="insights"
        title={t('marketing.features.s4Title')}
        lead={t('marketing.features.s4Lead')}
        image={IMG.coloredStack}
        imageAlt={t('marketing.features.s4ImageAlt')}
        points={[
          { icon: 'query_stats', title: t('marketing.features.s4p1Title'), text: t('marketing.features.s4p1Text') },
          { icon: 'star', title: t('marketing.features.s4p2Title'), text: t('marketing.features.s4p2Text') },
          { icon: 'notifications_active', title: t('marketing.features.s4p3Title'), text: t('marketing.features.s4p3Text') },
        ]}
      />

      {/* ===== 5. En equipo y tus datos ===== */}
      <section className="py-space-xl bg-surface-deck border-y border-border-subtle">
        <Container className="flex flex-col gap-space-xl">
          <SectionHeading
            eyebrow={t('marketing.features.teamEyebrow')}
            eyebrowIcon="verified_user"
            title={t('marketing.features.teamTitle')}
            subtitle={t('marketing.features.teamSubtitle')}
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-space-md">
            <FeatureCard icon="group" title={t('marketing.features.t1Title')} accent="amber">
              {t('marketing.features.t1Body')}
            </FeatureCard>
            <FeatureCard icon="upload_file" title={t('marketing.features.t2Title')} accent="amber">
              {t('marketing.features.t2Body')}
            </FeatureCard>
            <FeatureCard icon="download" title={t('marketing.features.t3Title')} accent="amber">
              {t('marketing.features.t3Body')}
            </FeatureCard>
            <FeatureCard icon="devices" title={t('marketing.features.t4Title')} accent="amber">
              {t('marketing.features.t4Body')}
            </FeatureCard>
          </div>
        </Container>
      </section>

      {/* ===== 6. Catálogo completo de herramientas y configuración ===== */}
      <section className="py-space-xl">
        <Container className="flex flex-col gap-space-xl">
          <SectionHeading
            eyebrow={t('marketing.features.allEyebrow')}
            eyebrowIcon="apps"
            title={t('marketing.features.allTitle')}
            subtitle={t('marketing.features.allSubtitle')}
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-space-md">
            <ToolList icon="build" title={t('marketing.features.toolsCatalogTitle')} items={catalogItems} />
            <ToolList icon="tune" title={t('marketing.features.toolsConfigTitle')} items={configItems} />
            <ToolList icon="admin_panel_settings" title={t('marketing.features.toolsAdminTitle')} items={adminItems} />
          </div>
        </Container>
      </section>

      <CtaBanner
        title={t('marketing.features.closingTitle')}
        subtitle={t('marketing.features.closingSubtitle')}
      />
    </>
  );
}
